import React from 'react';
import MuiButton from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

export interface ButtonProps {
  type?: 'primary' | 'default' | 'link' | 'dashed' | 'text' | 'ghost';
  danger?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
  size?: 'small' | 'middle' | 'large';
  block?: boolean;
  ghost?: boolean;
  shape?: 'circle' | 'round' | 'default';
  htmlType?: 'button' | 'submit' | 'reset';
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
  children?: React.ReactNode;
  hidden?: boolean;
  href?: string;
  target?: string;
  [key: string]: any;
}

function mapVariant(type?: ButtonProps['type']): 'contained' | 'outlined' | 'text' {
  switch (type) {
    case 'primary':
      return 'contained';
    case 'dashed':
    case 'default':
      return 'outlined';
    case 'link':
    case 'text':
    case 'ghost':
    default:
      return 'text';
  }
}

function mapSize(size?: ButtonProps['size']): 'small' | 'medium' | 'large' {
  if (size === 'large') return 'medium';
  if (size === 'middle') return 'medium';
  return 'small';
}

/**
 * Lưu ý: không spread `style` (CSSProperties số px) vào MUI `sx` —
 * sx sẽ nhân number theo theme spacing (×8) → height:28 thành 224px.
 */
export const Button: React.FC<ButtonProps> = ({
  type = 'default',
  danger,
  icon,
  loading,
  size = 'middle',
  block,
  htmlType = 'button',
  onClick,
  disabled,
  style,
  className,
  children,
  hidden,
  href,
  target,
  ghost,
  shape,
  ...rest
}) => {
  void ghost;
  void shape;
  void rest;
  if (hidden) return null;

  const variant = mapVariant(type);
  const color = danger ? 'error' : type === 'primary' ? 'primary' : 'inherit';

  const content = (
    <>
      {loading && (
        <CircularProgress size={14} color="inherit" sx={{ mr: children || icon ? 0.75 : 0 }} />
      )}
      {!loading && icon && (
        <Box
          component="span"
          sx={{
            mr: children ? 0.5 : 0,
            display: 'inline-flex',
            alignItems: 'center',
            lineHeight: 0,
            '& .MuiSvgIcon-root': {
              fontSize: size === 'small' ? 14 : 18,
            },
          }}
        >
          {icon}
        </Box>
      )}
      {children}
    </>
  );

  const sx = {
    textTransform: 'none' as const,
    ...(type === 'link' ? { minWidth: 0, p: 0.5 } : {}),
    ...(type === 'dashed' ? { borderStyle: 'dashed' } : {}),
  };

  if (href) {
    return (
      <MuiButton
        component="a"
        href={href}
        target={target}
        variant={variant}
        color={color as any}
        size={mapSize(size)}
        fullWidth={block}
        className={className}
        disabled={disabled || loading}
        onClick={onClick as any}
        style={style}
        sx={sx}
      >
        {content}
      </MuiButton>
    );
  }

  return (
    <MuiButton
      type={htmlType}
      variant={variant}
      color={color as any}
      size={mapSize(size)}
      fullWidth={block}
      className={className}
      disabled={disabled || loading}
      onClick={onClick}
      style={style}
      sx={sx}
    >
      {content}
    </MuiButton>
  );
};

export default Button;
